import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Icon from 'flarum/common/components/Icon';
import classList from 'flarum/common/utils/classList';
import extractText from 'flarum/common/utils/extractText';
import humanTime from 'flarum/common/helpers/humanTime';
import listItems from 'flarum/common/helpers/listItems';
import ItemList from 'flarum/common/utils/ItemList';
import { loadPreview, previewFor } from '../utils/previewStore';
const LARGE_MIN_WIDTH = 600;
const LARGE_MIN_RATIO = 1.2;
const LARGE_MAX_RATIO = 3;
function plain(text) {
  return (text ?? '').replace(/[\u202A-\u202E\u2066-\u2069]/g, '');
}
export default class LinkPreviewCard extends Component {
  imageFailed = false;
  deadFavicons = (() => new Set())();
  measuredLarge = null;
  clicks = null;
  oninit(vnode) {
    super.oninit(vnode);
    loadPreview(this.attrs.url, () => m.redraw());
  }
  view() {
    const state = previewFor(this.attrs.url);
    if (state.status === 'ready') {
      return this.viewReady(state.data);
    }
    return state.status === 'loading' ? this.viewLoading() : null;
  }
  viewLoading() {
    const label = extractText(app.translator.trans('datlechin-link-preview.forum.loading'));
    return m("a", Object.assign({}, this.anchorAttrs(['LinkPreview--compact', 'LinkPreview--loading'], label), {
      "aria-busy": "true"
    }), m("div", {
      className: "LinkPreview-body"
    }, m("div", {
      className: "fakeText"
    }), m("div", {
      className: "fakeText"
    })));
  }
  viewReady(data) {
    const image = this.imageFailed ? null : data.image;
    const layout = this.layout(data, image !== null);
    const host = this.host(data.url);
    const heading = plain(data.title);
    const siteName = heading === '' ? host : this.siteLabel(plain(data.siteName), heading, host);
    const description = plain(data.description);
    return m("a", this.anchorAttrs([`LinkPreview--${layout}`, 'fadeIn']), image && m("div", {
      className: "LinkPreview-media"
    }, m("img", {
      className: "LinkPreview-image",
      src: image.url,
      alt: "",
      "aria-hidden": "true",
      loading: "lazy",
      decoding: "async",
      onerror: () => {
        this.imageFailed = true;
        m.redraw();
      },
      onload: event => {
        if (event.target instanceof HTMLImageElement) {
          this.remeasure(data, event.target);
        }
      }
    })), m("div", {
      className: "LinkPreview-body"
    }, m("ul", {
      className: "LinkPreview-info"
    }, listItems(this.infoItems(data, siteName, host).toArray())), heading !== '' && m("div", {
      className: "LinkPreview-title"
    }, heading), description && m("div", {
      className: "LinkPreview-excerpt"
    }, description)));
  }
  infoItems(data, siteName, host) {
    const items = new ItemList();
    items.add('site', m("span", {
      className: "LinkPreview-site"
    }, this.viewFavicon(data, host), m("span", {
      className: "LinkPreview-siteName"
    }, siteName)), 100);
    const clicks = this.clickCount();
    if (clicks !== null) {
      items.add('clicks', m("span", null, app.translator.trans('datlechin-link-preview.forum.clicks', {
        count: clicks
      })), 95);
    }
    data.meta?.forEach((item, index) => {
      items.add(`meta${index}`, this.viewMeta(item), 90 - index);
    });
    return items;
  }
  viewMeta(item) {
    switch (item.type) {
      case 'count':
        return m("span", null, app.translator.trans(item.key, {
          count: item.count
        }));
      case 'date':
        return humanTime(new Date(item.date));
      default:
        return m("span", {
          className: item.type === 'user' ? 'username' : undefined
        }, plain(item.text));
    }
  }
  viewFavicon(data, host) {
    const src = this.faviconUrl(data, host);
    if (!src) {
      return m(Icon, {
        name: "fas fa-link",
        className: "LinkPreview-favicon"
      });
    }
    return m("img", {
      className: "LinkPreview-favicon",
      src: src,
      alt: "",
      "aria-hidden": "true",
      loading: "lazy",
      decoding: "async",
      onerror: () => {
        this.deadFavicons.add(src);
        m.redraw();
      }
    });
  }
  clickCount() {
    if (!this.attrs.link.classList.contains('LinkPreview-source')) {
      return null;
    }
    const raw = this.attrs.link.getAttribute('data-clicks');
    if (raw === null) {
      return null;
    }
    const count = Number.parseInt(raw, 10);
    return Number.isFinite(count) && count > 0 ? count : null;
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    if (!this.attrs.link.classList.contains('LinkPreview-source')) return;
    this.clicks = new MutationObserver(() => m.redraw());
    this.clicks.observe(this.attrs.link, {
      attributes: true,
      attributeFilter: ['data-clicks']
    });
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.clicks?.disconnect();
    this.clicks = null;
  }
  anchorAttrs(modifiers, label) {
    const link = this.attrs.link;
    const href = link.getAttribute('href') ?? this.attrs.url;
    const internal = this.attrs.internal;
    const rel = new Set((link.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean));
    if (!internal) {
      rel.add('noopener');
    }
    return {
      href,
      rel: rel.size ? Array.from(rel).join(' ') : undefined,
      target: internal || !this.setting('openLinksInNewTab', true) ? '_self' : '_blank',
      className: classList('LinkPreview', modifiers, this.sourceClasses(internal && this.hrefReachesDestination(href))),
      'aria-label': label,
      onclick: event => this.routeSamePage(event)
    };
  }
  routeSamePage(event) {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.defaultPrevented) {
      return;
    }
    const anchor = event.currentTarget;
    if (anchor.href !== window.location.href) return;
    event.preventDefault();
    m.route.set(anchor.pathname + anchor.search + anchor.hash);
  }
  sourceClasses(routable) {
    const source = this.attrs.link.classList;
    const kept = [];
    if (routable && source.contains('UrlLink--internal')) kept.push('UrlLink', 'UrlLink--internal');
    return kept;
  }
  hrefReachesDestination(href) {
    let url;
    let destination;
    try {
      url = new URL(href, document.baseURI);
      destination = new URL(this.attrs.url, document.baseURI);
    } catch {
      return false;
    }
    if (url.origin !== destination.origin) return false;
    const path = url.pathname;
    const target = destination.pathname;
    return path === target || path.startsWith(`${target}-`) || path.startsWith(`${target}/`);
  }
  siteLabel(siteName, title, host) {
    const name = siteName.trim().toLowerCase();
    const heading = title.trim().toLowerCase();
    if (name === '' || heading.startsWith(name) || heading.endsWith(name)) return host;
    return siteName;
  }
  layout(data, hasImage) {
    if (!hasImage) {
      return 'compact';
    }
    if (this.measuredLarge !== null) {
      return this.measuredLarge ? 'large' : 'compact';
    }
    return data.layout;
  }
  remeasure(data, image) {
    if (data.image?.width != null && data.image.height != null) {
      return;
    }
    const ratio = image.naturalHeight > 0 ? image.naturalWidth / image.naturalHeight : 0;
    const large = data.layout === 'large' || image.naturalWidth >= LARGE_MIN_WIDTH && ratio >= LARGE_MIN_RATIO && ratio <= LARGE_MAX_RATIO;
    if (this.measuredLarge === large) {
      return;
    }
    this.measuredLarge = large;
    m.redraw();
  }
  faviconUrl(data, host) {
    if (data.favicon && !this.deadFavicons.has(data.favicon)) {
      return data.favicon;
    }
    if (this.attrs.internal || !this.setting('googleFaviconFallback', false)) {
      return null;
    }
    const google = `https://www.google.com/s2/favicons?sz=64&domain_url=${encodeURIComponent(host)}`;
    return this.deadFavicons.has(google) ? null : google;
  }
  setting(name, fallback) {
    return app.forum.attribute(`datlechin-link-preview.${name}`) ?? fallback;
  }
  host(url) {
    if (url === void 0) {
      url = this.attrs.url;
    }
    try {
      return plain(new URL(url).host.replace(/^www\./, ''));
    } catch {
      return plain(url);
    }
  }
}
flarum.reg.add('datlechin-link-preview', 'forum/components/LinkPreviewCard', LinkPreviewCard);